import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import pl from "./pl";
import en from "./en";
import de from "./de";
import fr from "./fr";
import uk from "./uk";
import tr from "./tr";

export const LANGUAGES = [
  { code: "pl", label: "Polski" },
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "uk", label: "Українська" },
  { code: "tr", label: "Türkçe" },
];

export const DICTS = { pl, en, de, fr, uk, tr };

const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);

export function deepMerge(base, override) {
  if (Array.isArray(override)) return override;
  if (isPlainObject(base) && isPlainObject(override)) {
    const out = { ...base };
    for (const k of Object.keys(override)) out[k] = deepMerge(base[k], override[k]);
    return out;
  }
  return override === undefined ? base : override;
}

export function baseDict(lang) {
  return deepMerge(pl, DICTS[lang] || {});
}

export function flattenStrings(obj, prefix = "", out = []) {
  if (typeof obj === "string") out.push([prefix, obj]);
  else if (Array.isArray(obj)) obj.forEach((v, i) => flattenStrings(v, `${prefix}.${i}`, out));
  else if (isPlainObject(obj)) Object.keys(obj).forEach((k) => flattenStrings(obj[k], prefix ? `${prefix}.${k}` : k, out));
  return out;
}

export function applyOverrides(dict, overrides) {
  if (!overrides || !Object.keys(overrides).length) return dict;
  const out = JSON.parse(JSON.stringify(dict));
  for (const [path, value] of Object.entries(overrides)) {
    const parts = path.split(".");
    let cur = out;
    for (let i = 0; i < parts.length - 1; i++) {
      if (cur[parts[i]] === undefined || typeof cur[parts[i]] !== "object") cur = null;
      if (!cur) break;
      cur = cur[parts[i]];
    }
    if (cur && typeof cur[parts[parts.length - 1]] === "string") cur[parts[parts.length - 1]] = value;
  }
  return out;
}

const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("lang");
    if (fromUrl && DICTS[fromUrl]) return fromUrl;
    const saved = localStorage.getItem("tb-lang");
    return DICTS[saved] ? saved : "pl";
  });
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    localStorage.setItem("tb-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    api.get("/i18n").then((r) => setOverrides(r.data || {})).catch(() => {});
  }, []);

  const t = useMemo(() => applyOverrides(baseDict(lang), overrides[lang]), [lang, overrides]);

  return <LangContext.Provider value={{ t, lang, setLang }}>{children}</LangContext.Provider>;
}

export const useT = () => useContext(LangContext);
